import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { extractAWCDataFromEvent } from '@atlassian/analytics-bridge/extract-awc-data-from-event';
import { ActionSubject } from '@atlassian/proforma-utils/analytics/events';

import { NoticeType } from '../../components/Notice/noticeTypes';
import { type ApiError, constructApiError } from '../../models/ApiError';
import { type ErrorUtils } from '../../utils/ErrorUtils';

export type ResponseType = 'json' | 'blob' | 'text' | 'none';

export enum AcceptedResponseType {
	Json = 'json',
	Blob = 'blob',
}

export class ApiUtil {
	private readonly errorUtils: ErrorUtils;
	private readonly jiraUrl: string;
	private readonly proformaUrl: string;
	private readonly createAnalyticsEvent: CreateUIAnalyticsEvent;

	public constructor(
		jiraUrl: string,
		proformaUrl: string,
		errorUtils: ErrorUtils,
		createAnalyticsEvent: CreateUIAnalyticsEvent,
	) {
		this.jiraUrl = jiraUrl;
		this.proformaUrl = proformaUrl;
		this.errorUtils = errorUtils;
		this.createAnalyticsEvent = createAnalyticsEvent;
	}

	public get(
		path: string,
		requestName?: string,
		responseType: AcceptedResponseType = AcceptedResponseType.Json,
	): Promise<any> {
		return this.fetchApi(true, 'GET', path, responseType, requestName);
	}

	public jiraGet(
		path: string,
		requestName?: string,
		responseType: AcceptedResponseType = AcceptedResponseType.Json,
	): Promise<any> {
		return this.fetchApi(false, 'GET', path, responseType, requestName);
	}

	public put(
		path: string,
		body: any,
		requestName?: string,
		responseType: AcceptedResponseType | 'none' = AcceptedResponseType.Json,
	): Promise<any> {
		return this.fetchApi(true, 'PUT', path, responseType, requestName, body);
	}

	public jiraPut(
		path: string,
		body: any,
		requestName?: string,
		responseType: AcceptedResponseType | 'none' = AcceptedResponseType.Json,
	): Promise<any> {
		return this.fetchApi(false, 'PUT', path, responseType, requestName, body);
	}

	public post(
		path: string,
		body: any,
		requestName?: string,
		responseType: AcceptedResponseType = AcceptedResponseType.Json,
	): Promise<any> {
		return this.fetchApi(true, 'POST', path, responseType, requestName, body);
	}

	public patch(
		path: string,
		body: any,
		requestName?: string,
		responseType: AcceptedResponseType = AcceptedResponseType.Json,
	): Promise<any> {
		return this.fetchApi(true, 'PATCH', path, responseType, requestName, body);
	}

	public jiraPost(
		path: string,
		body: any,
		requestName?: string,
		responseType: AcceptedResponseType = AcceptedResponseType.Json,
	): Promise<any> {
		return this.fetchApi(false, 'POST', path, responseType, requestName, body);
	}

	public delete(
		path: string,
		body?: any,
		requestName?: string,
		responseType: ResponseType = 'none',
	): Promise<any> {
		return this.fetchApi(true, 'DELETE', path, responseType, requestName, body);
	}

	/** Converts the `relativePath` into an absolute ProForma API URI. */
	public createApiUrl(relativePath: string): string {
		return this.proformaUrl + (relativePath.charAt(0) === '/' ? '' : '/') + relativePath;
	}

	/** Converts the `relativePath` into an absolute Jira URI. */
	public createJiraUrl(relativePath: string): string {
		return this.jiraUrl + (relativePath.charAt(0) === '/' ? '' : '/') + relativePath;
	}

	getAnalyticsSource(): string {
		try {
			const analyticsEvent = this.createAnalyticsEvent({});
			const analyticsData = extractAWCDataFromEvent(analyticsEvent);
			return analyticsData?.payload?.source ?? 'unknown';
			/* eslint-disable no-unused-vars */
		} catch (e) {
			return 'unknown';
		}
	}

	protected fetchApi(
		proForma: boolean,
		method: string,
		path: string,
		responseType: ResponseType,
		requestName?: string,
		body?: any,
	): Promise<any> {
		const analyticsSource = this.getAnalyticsSource();
		return fetch(proForma ? this.createApiUrl(path) : this.createJiraUrl(path), {
			method,
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'X-Experimental-Experience': analyticsSource,
				...(responseType === AcceptedResponseType.Json ? { Accept: 'application/json' } : {}),
			},
			...(body && { body: JSON.stringify(body) }),
		})
			.catch((error) => {
				const message = `No response from API: ${error.message || ''}`;
				const apiError = toApiError(0, { error: message }, true, requestName);
				this.errorUtils.reportError(error, {
					actionSubject: ActionSubject.PROFORMA_API,
					id: 'fetchApi',
					attributes: {
						path,
						method,
						responseType,
						requestName,
						apiError: JSON.stringify(apiError),
						isClientFetchError: true,
					},
				});
				return Promise.reject(apiError);
			})
			.then((response) => {
				let errorObj: Error = new Error(response.statusText);
				if (!response.ok) {
					const traceId =
						response.headers.get('x-trace-id') ?? response.headers.get('atl-traceid') ?? undefined;
					let apiErrorPromise;
					try {
						// Try to parse response error details
						const responseJsonApiError =
							responseType !== 'json'
								? undefined
								: response
										.json()
										.then((responseJson: any) =>
											toApiError(response.status, responseJson, false, requestName, traceId),
										)
										.catch((error) => {
											const message = `Failed to parse error response as JSON from API: ${error.message || ''}`;
											errorObj = error as Error;
											return toApiError(
												response.status,
												{ error: message },
												false,
												requestName,
												traceId,
											);
										});
						if (response.status === 401) {
							this.errorUtils.notifyUser(NoticeType.ErrorApiUnauthorised);
						}
						if (response.status === 403) {
							this.errorUtils.notifyUser(NoticeType.ErrorApiMissingPermissions);
						}
						if (responseJsonApiError !== undefined) {
							apiErrorPromise = responseJsonApiError.then((apiError) => Promise.reject(apiError));
						} else {
							// Create error object with error response body
							apiErrorPromise = Promise.reject(
								toApiError(
									response.status,
									{ error: response.body || '' },
									false,
									requestName,
									traceId,
								),
							);
						}
					} catch (error) {
						const message = `Unexpected response from API: ${response.body || ''}`;
						errorObj = error as Error;
						apiErrorPromise = Promise.reject(
							toApiError(response.status, { error: message }, false, requestName, traceId),
						);
					}

					apiErrorPromise.catch((apiError) => {
						this.errorUtils.reportError(errorObj, {
							actionSubject: ActionSubject.PROFORMA_API,
							id: 'fetchApi',
							attributes: {
								status: response.status,
								statusGroup: `${Math.floor(response.status / 100)}xx`,
								traceId,
								path,
								method,
								responseType,
								requestName,
								apiError: JSON.stringify(apiError),
								isClientFetchError: false,
							},
						});
					});

					return apiErrorPromise;
				}
				if (response.status === 204) {
					return '';
				}
				switch (responseType) {
					case 'json':
						return response.json();
					case 'blob':
						return response.blob();
					case 'text':
						return response.text();
					case 'none':
						return '';
					default:
						return response;
				}
			});
	}
}

function toApiError(
	status: number,
	responseJson: any,
	isClientFetchError: boolean,
	requestName?: string,
	traceId?: string,
): ApiError {
	if (Object.keys(responseJson).length === 0) {
		return constructApiError(
			`Unexpected API reply (HTTP/${status})`,
			status,
			responseJson,
			requestName,
		);
	}
	const error = responseJson['error'] || `Unexpected API reply (HTTP/${status})`;
	// oxlint-disable-next-line prefer-destructuring
	const context = responseJson['context'];
	// oxlint-disable-next-line prefer-destructuring
	const contextId = responseJson['contextId'];
	// oxlint-disable-next-line prefer-destructuring
	const details = responseJson['details'];
	return constructApiError(
		error,
		status,
		responseJson,
		requestName,
		context,
		contextId,
		details,
		isClientFetchError,
		traceId,
	);
}
